<script setup lang="ts">
import KpModal from '@kp/geofy-vuejs-lib/tg-system/components/KpModal.vue';
import { computed, ref, watch, type Ref } from 'vue';
import { User } from '@/models/User';
import { appUrl, apiUrl } from '@/helpers/api';
import { emit as emitGlobalEvent } from '@/composables/useGlobalEventBus';
import { useRouter } from 'vue-router';
import { useBrowserStorage } from '@kp/geofy-vuejs-lib/tools/browser_local_storage.js';
import { globalVar } from '@/helpers/globals';

const show = defineModel<boolean>({ default: false });
const router = useRouter();
const ignoreChecked = ref(false);
const level2: Ref<boolean|null> = ref(null);

const props = withDefaults(
	defineProps<{
		user: User | null;
		redirectUrl?: string | null;
		route?: string | null;
		initialState?: number | null;
	}>(),
	{
		redirectUrl: appUrl('/baugesuche/new'),
		route: 'planungsgeschaefte-create',
		initialState: 0
	}
);

const show5 = ref(false);
const show7 = ref(false);
const show8 = ref(false);
const show10 = ref(false);
const show11 = ref(false);
const show16 = ref(false);
const show18 = ref(false);
const show19 = ref(false);

const consentFidentity: Ref<boolean> = ref(false);

const inBuerozeit = computed(() => {
	let now = new Date();
	if (now.getHours() < 8) {
		return false;
	}
	if (now.getHours() > 18) {
		return false;
	}
	if (now.getHours() === 17 && now.getMinutes() > 50) {
		return false;
	}

	return true;
});

const noShow = computed(() => {
	return (
		!show5.value &&
		!show7.value &&
		!show8.value &&
		!show10.value &&
		!show11.value &&
		!show16.value &&
		!show18.value &&
		!show19.value
	);
});

function setAllFalse() {
	show5.value = false;
	show7.value = false;
	show8.value = false;
	show10.value = false;
	show11.value = false;
	show16.value = false;
	show18.value = false;
	show19.value = false;
}

function setState(state: number) {
	setAllFalse();
	console.log(state);
	switch (state) {
		case 5:
			show5.value = true;
			break;
		case 7:
			show7.value = true;
			break;
		case 8:
			show8.value = true;
			break;
		case 10:
			show10.value = true;
			break;
		case 11:
			show11.value = true;
			break;
		case 16:
			show16.value = true;
			break;
		case 18:
			show18.value = true;
			break;
		case 19:
			show19.value = true;
			break;
	}
}

watch(show, (newShow, oldShow) => {
	if (newShow === oldShow) {
		return;
	}
	if (newShow) {
		open();
	} else {
		close();
	}
});

watch(noShow, (newVal, oldVal) => {
	if (newVal) {
		close();
	}
});

function open() {
	show.value = true;
	
	if (props.initialState !== null && props.initialState > 0) {
		setState(props.initialState);
	} else if (!props.user) {
		setState(18);
	} else if (!props.user?.has_username_passkey) {
		setState(19);
	} else {
		setState(0);
	}

	const { getStorageValue } = useBrowserStorage('eplan');
	if (getStorageValue('ignoreLot2Warning', false)) {
		ignoreChecked.value = true;
	}
}

function close() {
	setAllFalse();
	show.value = false;
}

function goToLogin() {
	emitGlobalEvent('login', props.redirectUrl);
	close();
	return;
}

function goToRegistrationExternal() {
	emitGlobalEvent('login', props.redirectUrl);
	//window.location.href = globalVar('iamregisterurl');
	close();
	return;
}

function finalizeFidentity() {
	consentFidentity.value = false;
	setAllFalse();
	document.location.href =
		apiUrl('/oidc2/login') +
		'?url=' +
		props.redirectUrl +
		'&acr_values=urn%3Acoreone%3Aauthentication%3Alot%3Avalue%3Aurn%3Akttg%3Aauthentication%3Alot%3A2';
}

function goToRoute() {
	setAllFalse();
	router.push({ name: props?.route || '' });
}

function toggleIgnore(val: boolean) {
	const { setStorageValue, clearStorageValue } = useBrowserStorage('eplan');
	ignoreChecked.value = val;
	if (val) {
		setStorageValue('ignoreLot2Warning', new Date().toISOString());
	} else {
		clearStorageValue('ignoreLot2Warning');
	}
}

function goToRegister() {
	let state = level2.value ? 5 : 7;
	setState(state);
}
</script>

<template>
	<KpModal v-model="show18">
		<h2 class="ktg-modal-title">{{ $t('user_login_circus_title_18') }}</h2>
		<div class="ktg-modal-text" v-html="$t('user_login_circus_text_18')" />

		<ktg-labelled-choice class="mt-0_5rem">
			<ktg-radio
				:checked="!level2 && level2 !== null"
				:value="false"
				input-id="lot1"
				@change="level2 = false"
			></ktg-radio>
			<ktg-label for="lot1">{{ $t('user_login_circus_lot1_label') }}</ktg-label>
		</ktg-labelled-choice>
		<ktg-labelled-choice class="mt-0_5rem">
			<ktg-radio :checked="level2" :value="true" input-id="lot2" @change="level2 = true"></ktg-radio>
			<ktg-label for="lot2">{{ $t('user_login_circus_lot2_label') }}</ktg-label>
		</ktg-labelled-choice>

		<ktg-link leading-icon="link" :href="appUrl('help/faq')">{{ $t('more_infos_account') }} </ktg-link>

		<ktg-modal-buttons vertical="" slot="buttons"
			><ktg-button
				appearance="primary"
				full-width=""
				size="small"
				@click="goToRegister"
				:disabled="level2 === null"
				>{{ $t('user_login_circus_register') }}</ktg-button
			><ktg-button
				:appearance="level2 !== null ? 'secondary' : 'primary'"
				full-width=""
				size="small"
				@click="goToLogin"
				>{{ $t('user_login_circus_to_login') }}</ktg-button
			></ktg-modal-buttons
		>
	</KpModal>
	<KpModal v-model="show19">
		<h2 class="ktg-modal-title">{{ $t('user_login_circus_title_19') }}</h2>
		<div class="ktg-modal-text" v-html="$t('user_login_circus_text_19')" />
		<ktg-link leading-icon="link" :href="appUrl('help/faq')">{{ $t('more_infos_account') }} </ktg-link>


		<ktg-clues class="mb-1_5rem">
			<ktg-clue>
				<ktg-clue-text>
					<ktg-labelled-choice>
						<ktg-checkbox
							input-id="ignoreNextTime"
							:checked="ignoreChecked"
							@change="toggleIgnore($event.target.checked)"
						></ktg-checkbox>
						<ktg-label for="ignoreNextTime">{{ $t('user_login_circus_dont_tell_me_again') }}</ktg-label>
					</ktg-labelled-choice>
				</ktg-clue-text>
			</ktg-clue></ktg-clues
		>
		<ktg-modal-buttons vertical="" slot="buttons"
			><ktg-button appearance="secondary" full-width="" size="small" @click="goToRoute">{{ $t('neein_danke_zum_baugesuch') }}</ktg-button
			><ktg-button appearance="secondary" full-width="" size="small" @click="setState(5)">{{
				$t('user_login_circus_register_lot2')
			}}</ktg-button
			></ktg-modal-buttons
		>
	</KpModal>
	<KpModal v-model="show5">
		<h2 class="ktg-modal-title">{{ $t('user_login_circus_title_05') }}</h2>
		<div class="ktg-modal-text" v-html="$t('user_login_circus_text_05')" />
		<div v-if="!inBuerozeit" class="ktg-modal-text" v-html="$t('user_login_circus_text_05_zusatz')" />
		<ktg-link leading-icon="link" :href="appUrl('help/faq')">{{ $t('more_infos_qes') }} </ktg-link>
		<ktg-modal-buttons vertical="" slot="buttons"
			><ktg-button appearance="primary" full-width="" size="small" @click="setState(8)">{{
				$t('show_signature_now')
			}}</ktg-button
			></ktg-modal-buttons
		>
	</KpModal>
	<KpModal v-model="show7">
		<h2 class="ktg-modal-title">{{ $t('user_login_circus_title_07') }}</h2>
		<div class="ktg-modal-text" v-html="$t('user_login_circus_text_07')" />
		<ktg-modal-buttons vertical="" slot="buttons"
			><ktg-button appearance="primary" full-width="" size="small" @click="goToRegistrationExternal">{{
				$t('user_login_circus_to_registration')
			}}</ktg-button></ktg-modal-buttons
		>
	</KpModal>
	<KpModal v-model="show8">
		<h2 class="ktg-modal-title">{{ $t('user_login_circus_title_08') }}</h2>
		<div class="ktg-modal-text" v-html="$t('user_login_circus_text_08')" />
		<ktg-labelled-choice>
			<ktg-checkbox
				:checked="consentFidentity"
				input-id="consentFidentity"
				autofocus
				@change="consentFidentity = $event.target?.checked"
			>
			</ktg-checkbox>
			<ktg-label for="consentFidentity" class="ktg-modal-text">{{ $t('consent_fidentity') }}</ktg-label>
		</ktg-labelled-choice>
		<ktg-modal-buttons vertical="" slot="buttons"
			><ktg-button
				appearance="primary"
				full-width=""
				size="small"
				:disabled="!consentFidentity"
				@click="finalizeFidentity"
				>{{ $t('weiter_zur_identifikation') }}</ktg-button
			><ktg-button appearance="danger" full-width="" size="small" @click="setState(10)">{{
				$t('ich_stimme_nicht_zu')
			}}</ktg-button></ktg-modal-buttons
		>
	</KpModal>

	<KpModal v-model="show10">
		<h2 class="ktg-modal-title">{{ $t('user_login_circus_title_10') }}</h2>
		<div class="ktg-modal-text" v-html="$t('user_login_circus_text_10')" />
		<ktg-labelled-choice>
			<ktg-checkbox
				:checked="consentFidentity"
				input-id="consentFidentity"
				autofocus
				@change="consentFidentity = $event.target?.checked"
			>
			</ktg-checkbox>
			<ktg-label for="consentFidentity" class="ktg-modal-text">{{ $t('consent_fidentity') }}</ktg-label>
		</ktg-labelled-choice>
		<ktg-modal-buttons vertical="" slot="buttons"
			><ktg-button
				appearance="primary"
				full-width=""
				size="small"
				:disabled="!consentFidentity"
				@click="finalizeFidentity"
				>{{ $t('weiter_zur_identifikation') }}</ktg-button
			><ktg-button appearance="danger" full-width="" size="small" @click="setState(11)">{{
				$t('ich_stimme_nicht_zu')
			}}</ktg-button></ktg-modal-buttons
		>
	</KpModal>

	<KpModal v-model="show11">
		<h2 class="ktg-modal-title">{{ $t('user_login_circus_title_11') }}</h2>
		<div class="ktg-modal-text" v-html="$t('user_login_circus_text_11')" />
		<ktg-modal-buttons vertical="" slot="buttons"
			><ktg-button appearance="primary" full-width="" size="small" @click="close">{{
				$t('close')
			}}</ktg-button></ktg-modal-buttons
		>
	</KpModal>
</template>
